import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { VSpacer } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';

import { Organization } from '../../../domain/organization/Organization';
import { Project } from '../../../domain/project/Project';
import { useUpdateOrganization } from '../../../hooks/organization/useUpdateOrganization';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import Button from '../../../react-components/Button';
import InputLocation from '../../../react-components/InputLocation';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';
import { AnimatedStep } from './AnimatedStep';

interface CompanyLocationProps {
  activeProject: Project;
  organization: Organization;
  organizationLocation?: Location;
  onChange: (organizationLocation: Location | undefined) => void;
  onNextStep: () => void;
}

export const CompanyLocation = ({
  activeProject,
  organization,
  organizationLocation,
  onChange,
  onNextStep,
}: CompanyLocationProps) => {
  const { success: organizationUpdated, run: updateOrganization } = useUpdateOrganization();
  const { success: projectUpdated, run: updateProject } = useUpdateProject({ project: activeProject });

  useEffect(() => {
    if (organizationUpdated) {
      updateProject({
        location: organizationLocation,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUpdated]);

  useEffect(() => {
    if (projectUpdated) {
      setTimeout(() => {
        onNextStep();
        eventsPanel.onboardingPageView({
          page: `companyLocation`,
          data: { companyLocation: organizationLocation?.label },
        });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUpdated]);

  return (
    <AnimatedStep id="companyLocationAnimatedContainer" isVisible={!organizationUpdated}>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ padding: `20px`, maxWidth: `660px`, position: `relative` }}
      >
        <Grid item>
          <Text variant="h6" intlId="company.onboarding.location.info" />
        </Grid>

        <VSpacer medium />

        <Grid item>
          <InputLocation
            fullWidth
            inputProps={{
              styles: {},
              size: `medium`,
              variant: `outlined`,
              placeholderIntlId: `company.onboarding.location.ph`,
            }}
            emptyIntlId="no_results"
            fieldValue={organizationLocation}
            onUpdate={(location?: Location | null) => {
              onChange(location ?? undefined);
            }}
          />
        </Grid>

        <Grid
          item
          container
          direction="row"
          wrap="nowrap"
          alignItems="center"
          justifyContent="center"
          style={{ position: `absolute`, bottom: `-60px` }}
        >
          {organizationLocation !== undefined && (
            <Button
              intlId="company.onboarding.next"
              style={{
                width: `200px`,
                height: `32px`,
              }}
              onClick={() => {
                updateOrganization({
                  location: organizationLocation,
                  country: organizationLocation.country_code,
                  organization,
                });
              }}
            />
          )}
        </Grid>
      </Grid>
    </AnimatedStep>
  );
};
